// ABOUTME: Splits a value into same/removed/added segments for every match of the find pattern.
// ABOUTME: Expands the replacement like String.prototype.replace and trims unchanged text to a context window.

export type SegmentKind = 'same' | 'removed' | 'added' | 'ellipsis';

export type Segment = { kind: SegmentKind; text: string };

export type DiffSegments = { segments: Segment[]; truncated: boolean };

/** Characters of unchanged text kept on each side of a match. */
export const DEFAULT_DIFF_CONTEXT = 60;

/** Pass as `context` to keep the whole value (the expanded view). */
export const FULL_DIFF_CONTEXT = Infinity;

const ELLIPSIS: Segment = { kind: 'ellipsis', text: '…' };

/**
 * Expands one `$` token of the replacement at `index` (which points at the `$`), following the
 * GetSubstitution steps of the spec: returns the text it stands for and how many characters it spans.
 */
const expandToken = (
    replace: string,
    index: number,
    match: RegExpMatchArray,
    input: string,
): { text: string; length: number } => {
    const next = replace[index + 1];
    const matched = match[0];
    const position = match.index ?? 0;
    const captureCount = match.length - 1;

    if (next === '$') return { text: '$', length: 2 };
    if (next === '&') return { text: matched, length: 2 };
    if (next === '`') return { text: input.slice(0, position), length: 2 };
    if (next === "'") return { text: input.slice(position + matched.length), length: 2 };

    if (next !== undefined && next >= '0' && next <= '9') {
        const twoDigits = /^\d\d/.test(replace.slice(index + 1));
        let digitCount = twoDigits ? 2 : 1;
        let captureIndex = Number(replace.slice(index + 1, index + 1 + digitCount));
        // `$10` with fewer than ten groups is `$1` followed by a literal `0`
        if (digitCount === 2 && captureIndex > captureCount) {
            digitCount = 1;
            captureIndex = Number(next);
        }
        if (captureIndex >= 1 && captureIndex <= captureCount) {
            return { text: match[captureIndex] ?? '', length: 1 + digitCount };
        }
        return { text: replace.slice(index, index + 1 + digitCount), length: 1 + digitCount };
    }

    if (next === '<') {
        const close = replace.indexOf('>', index + 2);
        if (!match.groups || close < 0) return { text: '$<', length: 2 };
        const name = replace.slice(index + 2, close);
        return { text: match.groups[name] ?? '', length: close - index + 1 };
    }

    return { text: '$', length: 1 };
};

/** The replacement text of one match, as String.prototype.replace would produce it. */
export function expandReplacement(match: RegExpMatchArray, replace: string, input: string): string {
    let result = '';
    let index = 0;
    while (index < replace.length) {
        if (replace[index] === '$') {
            const token = expandToken(replace, index, match, input);
            result += token.text;
            index += token.length;
        } else {
            const nextDollar = replace.indexOf('$', index);
            const end = nextDollar < 0 ? replace.length : nextDollar;
            result += replace.slice(index, end);
            index = end;
        }
    }
    return result;
}

const toGlobal = (regex: RegExp): RegExp =>
    regex.global ? regex : new RegExp(regex.source, `${regex.flags}g`);

/** Unchanged text before the first match: keeps its tail. */
const leadingContext = (text: string, context: number): Segment[] => {
    if (!text) return [];
    if (text.length <= context) return [{ kind: 'same', text }];
    return [ELLIPSIS, { kind: 'same', text: text.slice(text.length - context) }];
};

/** Unchanged text after the last match: keeps its head. */
const trailingContext = (text: string, context: number): Segment[] => {
    if (!text) return [];
    if (text.length <= context) return [{ kind: 'same', text }];
    return [{ kind: 'same', text: text.slice(0, context) }, ELLIPSIS];
};

/** Unchanged text between two matches: keeps both ends. */
const middleContext = (text: string, context: number): Segment[] => {
    if (!text) return [];
    if (text.length <= context * 2) return [{ kind: 'same', text }];
    return [
        { kind: 'same', text: text.slice(0, context) },
        ELLIPSIS,
        { kind: 'same', text: text.slice(text.length - context) },
    ];
};

export function computeDiffSegments(
    before: string,
    regex: RegExp,
    replace: string,
    context: number = DEFAULT_DIFF_CONTEXT,
): DiffSegments {
    const matches = Array.from(before.matchAll(toGlobal(regex)));

    if (!matches.length) {
        const segments = trailingContext(before, context);
        return { segments, truncated: segments.some(segment => segment.kind === 'ellipsis') };
    }

    const { segments, cursor } = matches.reduce<{ segments: Segment[]; cursor: number }>(
        (acc, match, matchIndex) => {
            const start = match.index ?? 0;
            const gap = before.slice(acc.cursor, start);
            const gapSegments =
                matchIndex === 0 ? leadingContext(gap, context) : middleContext(gap, context);
            const replacement = expandReplacement(match, replace, before);

            return {
                segments: [
                    ...acc.segments,
                    ...gapSegments,
                    ...(match[0] ? [{ kind: 'removed' as const, text: match[0] }] : []),
                    ...(replacement ? [{ kind: 'added' as const, text: replacement }] : []),
                ],
                cursor: start + match[0].length,
            };
        },
        { segments: [], cursor: 0 },
    );

    const allSegments = [...segments, ...trailingContext(before.slice(cursor), context)];
    return {
        segments: allSegments,
        truncated: allSegments.some(segment => segment.kind === 'ellipsis'),
    };
}
